import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AnimationDetailsDTO } from "../../dto/AnimationDetailsDTO";
import { AnimationDTO } from "../../dto/AnimationDTO";
import { RootState } from "../Store";

export interface AnimationLibraryState {
  isInitialized: boolean;
  filter: string;
  pagedAnimations: AnimationDTO[];
  selectedAnimation: AnimationDTO | null;
  selectedAnimationDetails: AnimationDetailsDTO | null;
  cachedAnimationDetails: Record<string, AnimationDetailsDTO>;
  cachedAnimationRenders: Record<string, string>;
}

export const unloadedAnimation: AnimationDTO = {
  name: "unloaded",
  animationState: "unloaded",
  layer: 0,
};

const initialAnimationLibraryState: AnimationLibraryState = {
  isInitialized: false,
  filter: "",
  pagedAnimations: [],
  selectedAnimation: null,
  selectedAnimationDetails: null,
  cachedAnimationDetails: {},
  cachedAnimationRenders: {},
};

export const animationLibrarySlice = createSlice({
  name: "animationLibrary",
  initialState: initialAnimationLibraryState,
  reducers: {
    initializeAnimationLibrary: (
      state,
      action: PayloadAction<{
        totalCount: number;
        firstPage: AnimationDTO[];
      }>
    ) => {
      const { totalCount, firstPage } = action.payload;
      state.isInitialized = true;
      state.pagedAnimations = new Array(totalCount).fill(unloadedAnimation);

      for (let i = 0; i < firstPage.length; i++) {
        state.pagedAnimations[i] = firstPage[i];
      }
    },
    updateAnimationLibrary: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        page: AnimationDTO[];
      }>
    ) => {
      const { pageNumber, page } = action.payload;
      state.pagedAnimations.splice(pageNumber * 10, 10, ...page);
    },
    setSelectedAnimation: (state, action: PayloadAction<AnimationDTO>) => {
      state.selectedAnimation = action.payload;
    },
    setAnimationLibraryFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setSelectedAnimationDetails: (
      state,
      action: PayloadAction<AnimationDetailsDTO>
    ) => {
      state.selectedAnimationDetails = action.payload;
    },
    setCachedAnimationDetails: (
      state,
      action: PayloadAction<{
        animationName: string;
        animationDetails: AnimationDetailsDTO;
      }>
    ) => {
      state.cachedAnimationDetails[action.payload.animationName] =
        action.payload.animationDetails;
    },
    setCachedAnimationRender: (
      state,
      action: PayloadAction<{
        animationName: string;
        downloadUri: string;
      }>
    ) => {
      state.cachedAnimationRenders[action.payload.animationName] =
        action.payload.downloadUri;
    },
    resetAnimationLibrary: () => initialAnimationLibraryState,
  },
});

export const {
  initializeAnimationLibrary,
  updateAnimationLibrary,
  setAnimationLibraryFilter,
  setSelectedAnimation,
  setSelectedAnimationDetails,
  setCachedAnimationDetails,
  setCachedAnimationRender,
  resetAnimationLibrary,
} = animationLibrarySlice.actions;

export const selectPagedAnimations = (state: RootState) =>
  state.animationLibrary.pagedAnimations;

export const selectAnimationLibraryFilter = (state: RootState) =>
  state.animationLibrary.filter;

export const selectSelectedAnimation = (state: RootState) =>
  state.animationLibrary.selectedAnimation;

export const selectSelectedAnimationDetails = (state: RootState) =>
  state.animationLibrary.selectedAnimationDetails;

export const selectCachedAnimationDetails = (state: RootState) =>
  state.animationLibrary.cachedAnimationDetails;

export const selectCachedAnimationDetailsByName = (
  state: RootState,
  animationName: string
) => state.animationLibrary.cachedAnimationDetails[animationName];

export const selectCachedAnimationRenderByName = (
  state: RootState,
  animationName: string
) => state.animationLibrary.cachedAnimationRenders[animationName];

export default animationLibrarySlice.reducer;
